import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Snackbar,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { verifyOtp, clearError } from "../../store/slices/authSlice";
import Toast from "../../components/common/Toast";

const validationSchema = Yup.object({
  otp: Yup.string()
    .required("OTP is required")
    .test('is-numeric', 'OTP must contain only numbers', (value) => {
      if (!value) return false;
      const cleanValue = value.replace(/\s/g, '');
      return /^\d+$/.test(cleanValue);
    })
    .test('is-6-digits', 'OTP must be exactly 6 digits', (value) => {
      if (!value) return false;
      const cleanValue = value.replace(/\s/g, '');
      return cleanValue.length === 6;
    }),
});

const toDisplayString = (val) => {
  if (typeof val === "string") return val;
  if (val && typeof val === "object" && "message" in val) return String(val.message);
  return val != null ? String(val) : "";
};

const OtpVerify = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error } = useSelector((state) => state.auth);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastSeverity, setToastSeverity] = useState("success");
  const [success, setSuccess] = useState(false);
  const email = location.state?.email;

  // Redirect if no email in state
  useEffect(() => {
    if (!email) {
      navigate("/forgot-password");
      setSuccess("Email not found");
      setToastMessage("Please enter a valid email address");
      setToastSeverity("error");
      return;
    }
  }, [email, navigate]);

  // Clear error on unmount
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const formik = useFormik({
    initialValues: { otp: "" },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      // Remove any spaces from OTP before submitting
      const cleanOtp = values.otp.replace(/\s/g, '');
      
      const result = await dispatch(
        verifyOtp({ email, otp: cleanOtp })
      );

      if (verifyOtp.fulfilled.match(result)) {
        // Extract token from response - check multiple possible locations
        const payload = result.payload;
        const token = payload?.token || 
                     payload?.resetToken || 
                     payload?.reset_token ||
                     payload?.data?.token ||
                     payload?.data?.resetToken ||
                     payload?.data?.reset_token ||
                     payload?.accessToken; // Some APIs return accessToken
        
      

        // Show success toast
        // Navigate after a short delay to show toast
        setTimeout(() => {
          navigate("/reset-password", { 
            replace: true, // Replace history so back button doesn't go to OTP page
            state: { 
              email,
              token: token || "otp-verified", // Use placeholder if no token, backend might use email+OTP
              otpVerified: true // Flag to indicate OTP was verified
            } 
          });
          const msg = result?.payload?.message || "OTP verified successfully";
          setSuccess("OTP verified successfully");
          setToastMessage(msg);
          setToastSeverity("success");
        }, 1500);
      } else if (verifyOtp.rejected.match(result)) {
        // Show error toast - payload is from rejectWithValue, may be string or object
        const errMsg = toDisplayString(result.payload ?? result.error) || "OTP verification failed. Please try again.";
        setToastMessage(errMsg);
        setToastSeverity("error");
        console.error("OTP verification failed:", result.payload ?? result.error);
      }
    },
  });

  // Handle OTP input - only allow numbers and format with space after 3 digits
  const handleOtpChange = (e) => {
    const value = e.target.value;
    // Remove all non-numeric characters
    const numericValue = value.replace(/\D/g, '');
    // Limit to 6 digits
    const limitedValue = numericValue.slice(0, 6);
    // Format as "123 456" (3 digits, space, 3 digits)
    let formattedValue = limitedValue;
    if (limitedValue.length > 3) {
      formattedValue = limitedValue.slice(0, 3) + ' ' + limitedValue.slice(3);
    }
    
    // Set the value and trigger validation
    formik.setFieldValue('otp', formattedValue, true);
  };

  const handleCloseToast = () => {
    setToastMessage(null);
  };

  if (!email) {
    return null; // Will redirect in useEffect
  }

  return (
    <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: "100%" }}>
      <Typography variant="h5" fontWeight={600} mb={1} textAlign="center">
        Verify OTP
      </Typography>

      <Typography variant="body2" textAlign="center" mb={3} color="text.secondary">
        Enter the 6-digit OTP sent to <strong>{email}</strong>
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {toDisplayString(error)}
        </Alert>
      )}

      <TextField
        fullWidth
        name="otp"
        label="OTP *"
        placeholder="123 456"
        value={formik.values.otp}
        onChange={handleOtpChange}
        onBlur={formik.handleBlur}
        error={formik.touched.otp && Boolean(formik.errors.otp)}
        helperText={
          formik.touched.otp && formik.errors.otp 
            ? formik.errors.otp 
            : formik.values.otp.replace(/\s/g, '').length === 6 
              ? "OTP is ready" 
              : `Enter 6-digit OTP (${formik.values.otp.replace(/\s/g, '').length}/6)`
        }
        inputProps={{
          maxLength: 7, // 6 digits + 1 space
          inputMode: "numeric",
          pattern: "[0-9 ]*",
        }}
        sx={{ 
          mb: 3,
          '& input': {
            textAlign: 'center',
            letterSpacing: '0.5em',
            fontSize: '1.5rem',
            fontWeight: 600,
          }
        }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading || formik.values.otp.replace(/\s/g, '').length !== 6}
        sx={{
          py: 1.5,
          mb: 2,
          position: "relative",
          "&:disabled": {
            backgroundColor: "primary.main",
            opacity: 0.7,
          },
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress
              size={20}
              thickness={4}
              sx={{
                color: "white",
                animationDuration: "550ms",
              }}
            />
            <Typography variant="body2" sx={{ color: "white" }}>
              Verifying...
            </Typography>
          </Box>
        ) : (
          "Verify OTP"
        )}
      </Button>

      <Box sx={{ textAlign: "center" }}>
        <Link
          component={RouterLink}
          to="/forgot-password"
          variant="body2"
          underline="hover"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          <ArrowBack fontSize="small" /> Back
        </Link>
      </Box>

      {/* Toast Notification */}
      <Snackbar
        open={!!toastMessage}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert 
          severity={toastSeverity} 
          onClose={handleCloseToast}
          sx={{ width: "100%" }}
        >
          {toDisplayString(toastMessage)}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OtpVerify;
